/**
 * The subscription page (`/subscribe`, web.md §4 "Additions"; billing.md "The mock provider"):
 * N selector 1–10, monthly / yearly, the price from `GET /api/billing/offers`, the limits of the chosen
 * offer next to the current plan's, and the purchase through `useServices().purchase` (the PurchaseFlow
 * seam). While the provider is the mock, a clearly labelled test-mode sheet stands in for the provider's
 * own checkout; Paddle / RevenueCat replace only the provider behind `purchase`.
 *
 * Signed-out users are sent through `/signin` first. On Pro, "Cancel plan" returns to Free at once (mock).
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { PlanLimits, PlanOffer } from '@digester/core';
import { DialogFrame, isDialogRegistered, openDialog, registerDialog, type DialogProps } from '../app/dialogs.js';
import { reportError } from '../app/errors.js';
import { planLabel } from '../app/hooks.js';
import { useServices } from '../app/services.js';
import { showToast } from '../app/Toast.js';
import { lastReaderPath } from '../app/navigation.js';
import { useEngineQuery } from '../engine-client/EngineProvider.js';
import { Icon } from '../icons/Icon.js';
import { formatExact } from '../ui/format.js';
import { formatDate, formatPrice, periodLabel, useAccountState } from './shared.js';

const MULTIPLIERS = Array.from({ length: 10 }, (_, i) => i + 1);

/** The rows of the limits table, in the order of billing.md's price table. */
const LIMIT_ROWS: ReadonlyArray<readonly [string, (l: PlanLimits) => string]> = [
  ['Sources', (l) => formatExact(l.sources)],
  ['New messages a day', (l) => formatExact(l.messagesPerDay)],
  ['Filters', (l) => formatExact(l.filters)],
  ['Topics', (l) => formatExact(l.topics)],
  ['Tags', (l) => formatExact(l.tags)],
  ['Folders', (l) => formatExact(l.folders)],
  ['Themes', (l) => `${formatExact(l.themes)}${l.customTheme ? ' + custom' : ''}`],
  ['AI scoring (Jev)', (l) => (l.aiScoring ? 'Included' : '—')],
  ['Synced devices', (l) => (l.syncDevices > 0 ? formatExact(l.syncDevices) : '—')],
  ['Assistant connections', (l) => (l.connectors > 0 ? formatExact(l.connectors) : '—')],
  ['Assistant requests a day', (l) => (l.connectorRequestsPerDay > 0 ? formatExact(l.connectorRequestsPerDay) : '—')],
];

interface MockCheckoutProps {
  offer: PlanOffer;
  onPay(): Promise<void>;
}

/** The mock provider's checkout sheet: test mode, nothing is charged. */
export function MockCheckoutDialog({ close, dialogId, offer, onPay }: DialogProps & MockCheckoutProps) {
  const [busy, setBusy] = useState(false);
  const plan = offer.multiplier > 1 ? `Pro ${offer.multiplier}` : 'Pro';
  return (
    <DialogFrame title="Test checkout" className="small-dialog mock-checkout" dialogId={dialogId} onClose={close}>
      <p className="test-mode-banner" role="note">
        <Icon name="flask-conical" />
        <span>
          <strong>Test mode — no money moves.</strong> Nothing is charged and no card is needed. Payments are not connected in this build.
        </span>
      </p>
      <dl className="checkout-summary">
        <div>
          <dt>Plan</dt>
          <dd>{plan}</dd>
        </div>
        <div>
          <dt>Billing</dt>
          <dd>{periodLabel(offer.period)}</dd>
        </div>
        <div>
          <dt>Price</dt>
          <dd>{formatPrice(offer.pricePerMonthCents, offer.currency)} / month</dd>
        </div>
        <div>
          <dt>Charged now</dt>
          <dd>{formatPrice(0, offer.currency)} (test)</dd>
        </div>
      </dl>
      <div className="dialog-footer">
        <button type="button" className="secondary-button" disabled={busy} onClick={close}>
          Cancel
        </button>
        <button
          type="button"
          className="primary-button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onPay();
              close();
            } catch (error) {
              reportError(error);
              setBusy(false);
            }
          }}
        >
          {busy ? 'Paying…' : 'Pay (test)'}
        </button>
      </div>
    </DialogFrame>
  );
}

function registerCheckoutDialog(): void {
  if (!isDialogRegistered('mock-checkout')) registerDialog<MockCheckoutProps>('mock-checkout', MockCheckoutDialog);
}

export function Subscribe() {
  const { purchase } = useServices();
  const account = useAccountState();
  const navigate = useNavigate();
  const offers = useEngineQuery(['billing', 'offers'], () => purchase.offers());
  const current = account.entitlement;
  const onPro = current?.plan === 'pro';
  const [period, setPeriod] = useState<'month' | 'year'>(current?.period ?? 'year');
  const [multiplier, setMultiplier] = useState<number>(onPro && current ? current.multiplier : 1);
  const offer = useMemo(() => offers.data?.find((o) => o.multiplier === multiplier && o.period === period) ?? null, [offers.data, multiplier, period]);
  const sameAsCurrent = onPro && current?.multiplier === multiplier && current.period === period;
  const yearlySaving = useMemo(() => {
    const m = offers.data?.find((o) => o.multiplier === 1 && o.period === 'month');
    const y = offers.data?.find((o) => o.multiplier === 1 && o.period === 'year');
    return m && y ? Math.round((1 - y.pricePerMonthCents / m.pricePerMonthCents) * 100) : null;
  }, [offers.data]);

  const buy = (): void => {
    if (!offer) return;
    if (!account.signedIn) {
      navigate('/signin?next=/subscribe');
      return;
    }
    const pay = async (): Promise<void> => {
      const outcome = await purchase.purchase({ multiplier: offer.multiplier, period: offer.period });
      if (outcome.status === 'purchased') {
        showToast(`You are on ${planLabel(outcome.entitlement)}`);
        navigate('/settings/account/plan');
      } else if (outcome.status === 'pending') showToast('The payment is being confirmed');
    };
    if (purchase.provider === 'mock') {
      registerCheckoutDialog();
      openDialog<MockCheckoutProps>('mock-checkout', { offer, onPay: pay });
    } else void pay().catch(reportError);
  };

  const cancelPlan = (): void => {
    openDialog('confirm', {
      title: 'Cancel plan',
      message: `Cancel ${planLabel(current)} and return to Free now?`,
      note: 'Sources, filters and topics beyond the Free limits are paused, not deleted. Sync and assistant connections stop.',
      confirmLabel: 'Cancel plan',
      cancelLabel: 'Keep Pro',
      onConfirm: async () => {
        try {
          const entitlement = await purchase.cancel();
          showToast(`You are on ${planLabel(entitlement)}`);
        } catch (error) {
          reportError(error);
        }
      },
    });
  };

  return (
    <div className="account-page">
      <section className="account-card subscribe-card" aria-labelledby="subscribe-title">
        <Link className="text-link account-back top" to={lastReaderPath()}>
          <Icon name="arrow-left" /> Back
        </Link>
        <span className="account-card-icon">
          <Icon name="badge-check" />
        </span>
        <h1 id="subscribe-title">Digester Pro</h1>
        <p>More sources and filters, Jev — AI scoring through our server — next to the free on-device model, sync between your devices, more themes and connections to your own assistant.</p>

        {onPro && current ? (
          <div className="current-plan" data-current-plan>
            <span>
              You are on <strong>{planLabel(current)}</strong> · {periodLabel(current.period).toLowerCase()}
              {current.validUntil ? ` · renews ${formatDate(current.validUntil)}` : ''}
            </span>
            <button type="button" className="secondary-button" onClick={cancelPlan}>
              Cancel plan
            </button>
          </div>
        ) : null}

        <div className="subscribe-controls">
          <div className="segmented" role="radiogroup" aria-label="Billing period">
            {(['month', 'year'] as const).map((p) => (
              <label key={p} className={period === p ? 'segment selected' : 'segment'}>
                <input type="radio" name="period" value={p} checked={period === p} onChange={() => setPeriod(p)} />
                <span>{p === 'month' ? 'Monthly' : 'Yearly'}</span>
                {p === 'year' && yearlySaving ? <small>save {yearlySaving}%</small> : null}
              </label>
            ))}
          </div>
          <label className="field multiplier-field">
            <span>Size · every limit × N</span>
            <select name="multiplier" value={multiplier} onChange={(e) => setMultiplier(Number(e.target.value))}>
              {MULTIPLIERS.map((n) => (
                <option key={n} value={n}>
                  {n === 1 ? 'Pro' : `Pro ${n}`}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="subscribe-price" aria-live="polite">
          {offer ? (
            <>
              <strong data-price>{formatPrice(offer.pricePerMonthCents, offer.currency)}</strong>
              <span> / month</span>
              <small>{offer.period === 'year' ? `${formatPrice(offer.chargeCents, offer.currency)} billed once a year` : 'Billed monthly'}</small>
            </>
          ) : offers.error ? (
            <small>{offers.error.message}</small>
          ) : (
            <small>Loading prices…</small>
          )}
        </div>

        <table className="limits-table">
          <thead>
            <tr>
              <th scope="col">Limit</th>
              <th scope="col">Now · {planLabel(current)}</th>
              <th scope="col">{multiplier > 1 ? `Pro ${multiplier}` : 'Pro'}</th>
            </tr>
          </thead>
          <tbody>
            {LIMIT_ROWS.map(([label, show]) => (
              <tr key={label}>
                <th scope="row">{label}</th>
                <td>{current ? show(current.limits) : '—'}</td>
                <td>{offer ? show(offer.limits) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="account-actions">
          <button type="button" className="primary-button" disabled={!offer || sameAsCurrent} onClick={buy}>
            {!account.signedIn ? 'Sign in to subscribe' : sameAsCurrent ? 'Your current plan' : onPro ? 'Change plan' : 'Subscribe'}
          </button>
        </div>
        {purchase.provider === 'mock' ? <p className="field-help">Payments run in test mode in this build: nothing is charged.</p> : null}
      </section>
    </div>
  );
}
